import { useCallback, useContext, useMemo } from "react";
import {
    OutstandingCashItem,
    ReconciliationUnmatchedBehavior,
    TransactionsPairForReconciliation,
} from "@common/contracts/reconciliation.contract";
import { FinancialAccount } from "@common/types/financialAccount";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import {
    getStatmentTransactionId,
    pairHasDifference,
} from "@common/helpers/reconciliation";
import { ReconciliationContext } from "./Reconciliation.context";
import {
    pairDifferenceIsAccepted,
    RECONCILIATION_PRIMARY_HOTKEY,
    ReconciliationReviewMode,
} from "./helpers";
import { useFinishReconciliation } from "./useFinishReconciliation";
import { ReconciliationCloseButton } from "./ReconciliationCloseButton";
import { ReconciliationFinishButton } from "./ReconciliationFinishButton";

const NO_CASH_ITEMS_TO_CONFIRM: OutstandingCashItem[] = [];

interface Props {
    pairs: TransactionsPairForReconciliation[];
    financialAccount: FinancialAccount;
    onHide: () => void;
    onBack: () => void;
    onSuccess?: () => void;
    isBalanced: boolean;
    goToNextChange: () => void;
    mode: ReconciliationReviewMode;
}

export const ReconciliationMatchFooter: React.FC<Props> = ({
    onHide,
    onBack,
    onSuccess,
    pairs,
    financialAccount,
    isBalanced,
    goToNextChange,
    mode,
}) => {
    const {
        financialTransactionsToRemoveIdsSet,
        statementTransactionsToAddIdsSet,
        registerFinancialTransactionToRemove,
        registerStatementTransactionToAdd,
        isReconciliationComplete,
        notReconciledFinancialTransactionsIdsSet,
        registerNotReconciledFinancialTransaction,
        financialTransactionsFromPreviousPeriodIds,
    } = useContext(ReconciliationContext);
    const requiringAcceptance = useMemo(
        () =>
            pairs.filter(
                (pair) =>
                    pairHasDifference(pair) &&
                    !pairDifferenceIsAccepted({
                        pair,
                        financialTransactionsToRemoveIdsSet,
                        statementTransactionsToAddIdsSet,
                        notReconciledFinancialTransactionsIdsSet,
                        financialTransactionsFromPreviousPeriodIds,
                    }),
            ),
        [
            pairs,
            financialTransactionsToRemoveIdsSet,
            statementTransactionsToAddIdsSet,
            notReconciledFinancialTransactionsIdsSet,
            financialTransactionsFromPreviousPeriodIds,
        ],
    );

    const acceptAll = useCallback(() => {
        requiringAcceptance.forEach((pair) => {
            if (
                pair.fromAccount &&
                pair.fromAccount.unmatchedBehavior ===
                    ReconciliationUnmatchedBehavior.REMOVE
            ) {
                registerFinancialTransactionToRemove(
                    pair.fromAccount.financialTransactionId,
                );
            }
            if (
                pair.fromAccount &&
                pair.fromAccount.unmatchedBehavior ===
                    ReconciliationUnmatchedBehavior.NOT_RECONCILED
            ) {
                registerNotReconciledFinancialTransaction(
                    pair.fromAccount.financialTransactionId,
                );
            }
            if (pair.fromStatement) {
                registerStatementTransactionToAdd(
                    getStatmentTransactionId(pair.fromStatement),
                );
            }
        });
    }, [
        requiringAcceptance,
        registerFinancialTransactionToRemove,
        registerStatementTransactionToAdd,
        registerNotReconciledFinancialTransaction,
    ]);

    const { finish, isFinishing } = useFinishReconciliation({
        pairs,
        financialAccount,
    });

    const cta = useMemo(() => {
        if (isReconciliationComplete) {
            return (
                <ReconciliationCloseButton
                    onHide={onHide}
                    onSuccess={onSuccess}
                />
            );
        } else if (
            requiringAcceptance.length === 0 &&
            mode.kind === "workflow-match"
        ) {
            return (
                <WithHotkey
                    hotKey={RECONCILIATION_PRIMARY_HOTKEY}
                    hotKeyOptions={{ description: "Continue" }}
                    hotKeyDisplayPosition="inline"
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={mode.onContinue}
                            rightIcon={hotkeyHint}
                        >
                            Continue
                        </Button>
                    )}
                </WithHotkey>
            );
        } else if (requiringAcceptance.length === 0) {
            return (
                <ReconciliationFinishButton
                    variant="primary"
                    outstandingCashItems={NO_CASH_ITEMS_TO_CONFIRM}
                    onFinish={finish}
                    isFinishing={isFinishing}
                    disabled={!isBalanced}
                    hotKey={RECONCILIATION_PRIMARY_HOTKEY}
                />
            );
        }
        return (
            <WithHotkey
                hotKey={RECONCILIATION_PRIMARY_HOTKEY}
                hotKeyOptions={{
                    description: "Accept all changes",
                }}
                hotKeyDisplayPosition="inline"
            >
                {(hotkeyHint) => (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={acceptAll}
                        rightIcon={hotkeyHint}
                    >
                        Accept all
                    </Button>
                )}
            </WithHotkey>
        );
    }, [
        isReconciliationComplete,
        requiringAcceptance,
        mode,
        finish,
        isFinishing,
        isBalanced,
        onHide,
        onSuccess,
        acceptAll,
    ]);

    return (
        <CommandIsland sticky alignEnd>
            {!isReconciliationComplete && (
                <>
                    <WithHotkey
                        hotKey="space"
                        hotKeyOptions={{
                            description: "Go to next change",
                            preventDefault: true,
                        }}
                        hotKeyDisplayPosition="inline"
                    >
                        {(hotkeyHint) => (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={goToNextChange}
                                className="mr-auto"
                                rightIcon={hotkeyHint}
                            >
                                Go to next change
                            </Button>
                        )}
                    </WithHotkey>

                    <Button variant="primary" size="md" onClick={onBack}>
                        Back
                    </Button>
                </>
            )}
            {cta}
        </CommandIsland>
    );
};
